export interface CultureHighlight {
  name: string;
  location: string;
  image: string;
  description: string;
}

export interface CultureUniverse {
  id: string;
  title: string;
  tagline: string;
  icon: string;
  heroImage: string;
  description: string;
  stats: { value: string; label: string }[];
  tags: string[];
  highlights: CultureHighlight[];
}

export const cultureUniverses: CultureUniverse[] = [
  {
    id: 'art-artisanat',
    title: 'Art & Artisanat',
    tagline: "Le savoir-faire des maîtres artisans",
    icon: 'ri-palette-line',
    heroImage:
      'https://readdy.ai/api/search-image?query=Vibrant%20African%20artisan%20workshop%20filled%20with%20handwoven%20baskets%20carved%20wooden%20masks%20and%20painted%20clay%20pottery%2C%20warm%20golden%20light%20streaming%20through%20window%2C%20rich%20terracotta%20and%20ochre%20tones%2C%20traditional%20craftsmanship%20heritage%2C%20professional%20editorial%20photography%2C%20simple%20warm%20earthy%20background&width=1200&height=800&seq=culture-page-art&orientation=landscape',
    description:
      "Des mains qui façonnent la matière et transmettent des siècles de gestes. Sculpture sur bois, poterie, batik, tissage ou vannerie : chaque pièce raconte une histoire et porte l'empreinte d'un artisan passionné. Découvrez les ateliers, galeries et créateurs qui font rayonner l'artisanat africain aux quatre coins du monde.",
    stats: [
      { value: '180+', label: 'Artisans référencés' },
      { value: '12', label: "Métiers d'art" },
      { value: '5', label: 'Pays représentés' },
    ],
    tags: ['Sculpture', 'Batik', 'Poterie', 'Vannerie', 'Tissage'],
    highlights: [
      {
        name: 'Atelier Kossi Sculpture',
        location: 'Lomé, Togo',
        image:
          'https://readdy.ai/api/search-image?query=Master%20African%20wood%20carver%20sculpting%20an%20intricate%20mask%20in%20his%20workshop%2C%20wood%20shavings%20and%20tools%20on%20table%2C%20warm%20golden%20light%2C%20rich%20earthy%20tones%2C%20traditional%20craftsmanship%2C%20professional%20photography%2C%20simple%20warm%20background&width=600&height=450&seq=culture-art-1&orientation=landscape',
        description: 'Sculptures sur bois d\'ébène et de teck, pièces uniques signées.',
      },
      {
        name: 'Tissus Batik Aïcha',
        location: 'Cotonou, Bénin',
        image:
          'https://readdy.ai/api/search-image?query=Colorful%20handmade%20batik%20fabrics%20hanging%20to%20dry%20with%20intricate%20wax%20patterns%2C%20bright%20warm%20colors%2C%20artisan%20textile%20workshop%2C%20professional%20photography%2C%20simple%20warm%20background&width=600&height=450&seq=culture-art-2&orientation=landscape',
        description: 'Batiks teints à la main aux motifs traditionnels et contemporains.',
      },
      {
        name: 'Poterie de Kpalimé',
        location: 'Kpalimé, Togo',
        image:
          'https://readdy.ai/api/search-image?query=African%20potter%20shaping%20a%20clay%20pot%20on%20a%20traditional%20wheel%2C%20rows%20of%20finished%20terracotta%20pots%20in%20warm%20light%2C%20rustic%20workshop%2C%20professional%20photography%2C%20warm%20earthy%20background&width=600&height=450&seq=culture-art-3&orientation=landscape',
        description: 'Céramiques et poteries utilitaires façonnées à la terre cuite.',
      },
    ],
  },
  {
    id: 'musique-danse',
    title: 'Musique & Danse',
    tagline: "Une scène vibrante et pleine de talent",
    icon: 'ri-music-2-line',
    heroImage:
      'https://readdy.ai/api/search-image?query=Traditional%20West%20African%20drummers%20and%20dancers%20performing%20on%20stage%20at%20golden%20sunset%2C%20djembe%20drums%2C%20colorful%20traditional%20clothing%2C%20energetic%20joyful%20atmosphere%2C%20warm%20orange%20light%2C%20professional%20concert%20photography%2C%20warm%20earthy%20background&width=1200&height=800&seq=culture-page-music&orientation=landscape',
    description:
      "Des rythmes du djembé aux sonorités modernes, la musique et la danse sont le cœur battant de la culture. Artistes, groupes, DJ, écoles de danse et festivals : plongez dans une scène vivante où la tradition rencontre l'innovation, et laissez-vous porter par l'énergie des performances.",
    stats: [
      { value: '240+', label: 'Artistes & groupes' },
      { value: '35', label: 'Festivals annuels' },
      { value: '20+', label: 'Écoles de danse' },
    ],
    tags: ['Djembé', 'Concert', 'Festival', 'Danse', 'Studio'],
    highlights: [
      {
        name: 'Festival des Rythmes',
        location: 'Abidjan, Côte d\'Ivoire',
        image:
          'https://readdy.ai/api/search-image?query=Large%20outdoor%20African%20music%20festival%20crowd%20dancing%20at%20dusk%2C%20stage%20lights%20and%20djembe%20drums%2C%20vibrant%20colorful%20atmosphere%2C%20warm%20golden%20light%2C%20professional%20event%20photography%2C%20simple%20warm%20background&width=600&height=450&seq=culture-music-1&orientation=landscape',
        description: 'Le rendez-vous incontournable des musiques d\'Afrique de l\'Ouest.',
      },
      {
        name: 'École Djembé Académie',
        location: 'Dakar, Sénégal',
        image:
          'https://readdy.ai/api/search-image?query=African%20drumming%20class%20with%20students%20learning%20djembe%20rhythms%2C%20teacher%20demonstrating%20technique%2C%20warm%20studio%20lighting%2C%20joyful%20learning%20atmosphere%2C%20professional%20photography%2C%20warm%20earthy%20background&width=600&height=450&seq=culture-music-2&orientation=landscape',
        description: 'Cours de percussion traditionnelle pour tous les niveaux.',
      },
      {
        name: 'Groupe Afro Fusion',
        location: 'Accra, Ghana',
        image:
          'https://readdy.ai/api/search-image?query=Modern%20African%20band%20performing%20on%20stage%20with%20traditional%20and%20electric%20instruments%2C%20colorful%20stage%20lighting%2C%20energetic%20performance%2C%20professional%20concert%20photography%2C%20warm%20tones%2C%20simple%20background&width=600&height=450&seq=culture-music-3&orientation=landscape',
        description: 'La fusion audacieuse du traditionnel et des sonorités urbaines.',
      },
    ],
  },
  {
    id: 'gastronomie',
    title: 'Gastronomie',
    tagline: "Une cuisine authentique qui raconte une histoire",
    icon: 'ri-restaurant-2-line',
    heroImage:
      'https://readdy.ai/api/search-image?query=Delicious%20West%20African%20feast%20served%20in%20rustic%20clay%20bowls%2C%20fufu%2C%20peanut%20sauce%2C%20grilled%20fish%20and%20jollof%20rice%2C%20colorful%20fresh%20spices%20and%20ingredients%2C%20warm%20ambient%20lighting%2C%20wooden%20table%2C%20professional%20food%20photography%2C%20rich%20warm%20tones%2C%20simple%20dark%20background&width=1200&height=800&seq=culture-page-food&orientation=landscape',
    description:
      "Fufu, akoumé, sauce d'arachide, poisson braisé ou jollof rice : la gastronomie africaine est un voyage des sens. Maquis conviviaux, restaurants gastronomiques, traiteurs et chefs passionnés : savourez des plats généreux qui rassemblent les familles et célèbrent le partage.",
    stats: [
      { value: '300+', label: 'Restaurants & maquis' },
      { value: '45', label: 'Chefs & traiteurs' },
      { value: '80+', label: 'Recettes traditionnelles' },
    ],
    tags: ['Maquis', 'Cuisine locale', 'Saveurs', 'Traiteur', 'Gastronomie'],
    highlights: [
      {
        name: 'Maquis Chez Ama',
        location: 'Lomé, Togo',
        image:
          'https://readdy.ai/api/search-image?query=Cozy%20African%20maquis%20restaurant%20interior%20with%20wooden%20tables%20and%20warm%20lantern%20light%2C%20plates%20of%20grilled%20fish%20and%20fufu%2C%20welcoming%20atmosphere%2C%20professional%20food%20photography%2C%20warm%20tones%2C%20simple%20background&width=600&height=450&seq=culture-food-1&orientation=landscape',
        description: 'Un maquis chaleureux réputé pour son poisson braisé à la braise.',
      },
      {
        name: 'Table du Terroir',
        location: 'Ouagadougou, Burkina Faso',
        image:
          'https://readdy.ai/api/search-image?query=Elegant%20African%20gastronomic%20restaurant%20plating%20a%20refined%20dish%20with%20local%20ingredients%2C%20warm%20ambient%20light%2C%20chef%20presenting%20food%2C%20professional%20food%20photography%2C%20warm%20earthy%20tones%2C%20simple%20background&width=600&height=450&seq=culture-food-2&orientation=landscape',
        description: 'La cuisine de terroir revisitée avec finesse et créativité.',
      },
      {
        name: 'Traiteur Saveurs d\'Afrique',
        location: 'Cotonou, Bénin',
        image:
          'https://readdy.ai/api/search-image?query=Beautifully%20arranged%20African%20catering%20buffet%20with%20colorful%20traditional%20dishes%20and%20fresh%20fruit%2C%20warm%20festive%20lighting%2C%20professional%20food%20photography%2C%20warm%20tones%2C%20simple%20background&width=600&height=450&seq=culture-food-3&orientation=landscape',
        description: 'Catering pour événements et cérémonies, du buffet au service.',
      },
    ],
  },
  {
    id: 'mode-wax',
    title: 'Mode & Wax',
    tagline: "Tradition et modernité portées avec fierté",
    icon: 'ri-t-shirt-line',
    heroImage:
      'https://readdy.ai/api/search-image?query=Elegant%20African%20fashion%20models%20wearing%20vibrant%20wax%20print%20ankara%20dresses%2C%20colorful%20patterns%20and%20gold%20jewelry%2C%20warm%20studio%20lighting%2C%20modern%20editorial%20photography%2C%20rich%20warm%20tones%2C%20clean%20simple%20background&width=1200&height=800&seq=culture-page-fashion&orientation=landscape',
    description:
      "Le wax, le pagne et les tissus traditionnels se réinventent chaque saison. Créateurs, stylistes, tailleurs et accessoiristes donnent vie à une mode audacieuse qui célèbre l'identité. Du sur-mesure à la collection prêt-à-porter, habillez-vous de couleurs et de fierté.",
    stats: [
      { value: '150+', label: 'Créateurs & tailleurs' },
      { value: '28', label: 'Marques de mode' },
      { value: '60+', label: 'Ateliers de couture' },
    ],
    tags: ['Wax', 'Sur-mesure', 'Accessoires', 'Styliste', 'Défilé'],
    highlights: [
      {
        name: 'Maison Kemi Wax',
        location: 'Dakar, Sénégal',
        image:
          'https://readdy.ai/api/search-image?query=African%20fashion%20designer%20adjusting%20a%20colorful%20wax%20print%20dress%20on%20a%20mannequin%20in%20a%20bright%20atelier%2C%20fabric%20rolls%20and%20sketches%2C%20warm%20studio%20light%2C%20professional%20editorial%20photography%2C%20simple%20background&width=600&height=450&seq=culture-fashion-1&orientation=landscape',
        description: 'Créations en wax haut de gamme, du sur-mesure à la collection.',
      },
      {
        name: 'Atelier Couture Nana',
        location: 'Abidjan, Côte d\'Ivoire',
        image:
          'https://readdy.ai/api/search-image?query=African%20seamstress%20sewing%20a%20vibrant%20ankara%20garment%20on%20a%20sewing%20machine%2C%20colorful%20fabrics%20around%2C%20warm%20natural%20light%2C%20professional%20photography%2C%20warm%20tones%2C%20simple%20background&width=600&height=450&seq=culture-fashion-2&orientation=landscape',
        description: 'Tailleur spécialisé en tenues traditionnelles et modernes.',
      },
      {
        name: 'Accessoires Adjoa',
        location: 'Accra, Ghana',
        image:
          'https://readdy.ai/api/search-image?query=Beautiful%20handmade%20African%20beaded%20jewelry%20and%20gold%20accessories%20displayed%2C%20colorful%20beads%20and%20brass%20pieces%2C%20warm%20lighting%2C%20professional%20product%20photography%2C%20simple%20warm%20background&width=600&height=450&seq=culture-fashion-3&orientation=landscape',
        description: 'Bijoux et accessoires artisanaux en perles et laiton.',
      },
    ],
  },
  {
    id: 'traditions-festivals',
    title: 'Traditions & Festivals',
    tagline: "L'âme vivante des peuples",
    icon: 'ri-flag-2-line',
    heroImage:
      'https://readdy.ai/api/search-image?query=Colorful%20traditional%20African%20festival%20celebration%20with%20dancers%20in%20vibrant%20ceremonial%20costumes%20and%20masks%2C%20golden%20hour%20light%2C%20joyful%20cultural%20heritage%2C%20drums%20and%20music%2C%20professional%20photography%2C%20warm%20earthy%20tones&width=1200&height=800&seq=culture-page-tradition&orientation=landscape',
    description:
      "Cérémonies, masques, danses ancestrales et rites initiatiques : les traditions sont la mémoire vivante de nos peuples. Festivals, événements culturels, guides du patrimoine et gardiens de la mémoire vous invitent à vivre des moments authentiques et profondément humains.",
    stats: [
      { value: '50+', label: 'Festivals culturels' },
      { value: '90+', label: 'Événements annuels' },
      { value: '25', label: 'Troupes & collectifs' },
    ],
    tags: ['Cérémonie', 'Masques', 'Héritage', 'Rituel', 'Festival'],
    highlights: [
      {
        name: 'Festival des Masques',
        location: 'Man, Côte d\'Ivoire',
        image:
          'https://readdy.ai/api/search-image?query=Traditional%20African%20masked%20dancers%20performing%20a%20sacred%20ritual%2C%20elaborate%20colorful%20masks%20and%20costumes%2C%20warm%20sunset%20light%2C%20cultural%20heritage%20ceremony%2C%20professional%20photography%2C%20warm%20earthy%20tones&width=600&height=450&seq=culture-tradition-1&orientation=landscape',
        description: 'Célébration spectaculaire des masques sacrés et danses rituelles.',
      },
      {
        name: 'Cérémonie Vodoun',
        location: 'Ouidah, Bénin',
        image:
          'https://readdy.ai/api/search-image?query=Traditional%20African%20voodoo%20ceremony%20with%20spiritual%20dancers%20and%20offerings%2C%20colorful%20ceremonial%20attire%2C%20warm%20fire%20light%2C%20cultural%20ritual%2C%20professional%20photography%2C%20warm%20earthy%20background&width=600&height=450&seq=culture-tradition-2&orientation=landscape',
        description: 'Immersion dans les rites et la spiritualité ancestrale.',
      },
      {
        name: 'Griot du Patrimoine',
        location: 'Kayes, Mali',
        image:
          'https://readdy.ai/api/search-image?query=African%20griot%20storyteller%20sharing%20oral%20history%20with%20attentive%20listeners%20around%20a%20fire%2C%20warm%20golden%20light%2C%20traditional%20setting%2C%20cultural%20heritage%2C%20professional%20photography%2C%20warm%20earthy%20tones&width=600&height=450&seq=culture-tradition-3&orientation=landscape',
        description: 'Récits et chants transmis de génération en génération.',
      },
    ],
  },
  {
    id: 'tourisme-patrimoine',
    title: 'Tourisme & Patrimoine',
    tagline: "Explorez un patrimoine unique au monde",
    icon: 'ri-landscape-line',
    heroImage:
      'https://readdy.ai/api/search-image?query=Breathtaking%20traditional%20African%20mud-brick%20architecture%20heritage%20site%20with%20geometric%20patterns%2C%20palm%20trees%20and%20warm%20sunset%20light%2C%20cultural%20tourism%2C%20professional%20travel%20photography%2C%20warm%20golden%20tones%2C%20simple%20background&width=1200&height=800&seq=culture-page-tourism&orientation=landscape',
    description:
      "Sites historiques, architectures traditionnelles, paysages grandioses et villages authentiques : le patrimoine africain regorge de trésors à découvrir. Guides passionnés, agences locales et hébergements de charme vous accompagnent pour une aventure inoubliable au cœur de l'histoire.",
    stats: [
      { value: '40+', label: 'Sites patrimoniaux' },
      { value: '70+', label: 'Guides & agences' },
      { value: '15', label: 'Villages à découvrir' },
    ],
    tags: ['Patrimoine', 'Sites', 'Découverte', 'Guide', 'Voyage'],
    highlights: [
      {
        name: 'Cases de Koutammakou',
        location: 'Kara, Togo',
        image:
          'https://readdy.ai/api/search-image?query=Traditional%20Tammari%20mud%20tower%20houses%20in%20Koutammakou%20Togo%2C%20UNESCO%20heritage%2C%20golden%20sunset%20light%2C%20lush%20green%20landscape%2C%20professional%20travel%20photography%2C%20warm%20tones%2C%20simple%20background&width=600&height=450&seq=culture-tourism-1&orientation=landscape',
        description: 'Site classé UNESCO, l\'habitat traditionnel des Tamberma.',
      },
      {
        name: 'Grande Mosquée de Djenné',
        location: 'Djenné, Mali',
        image:
          'https://readdy.ai/api/search-image?query=The%20Great%20Mosque%20of%20Djenne%20in%20Mali%2C%20magnificent%20mud-brick%20Sudano-Sahelian%20architecture%2C%20warm%20golden%20light%2C%20professional%20travel%20photography%2C%20warm%20earthy%20tones%2C%20simple%20background&width=600&height=450&seq=culture-tourism-2&orientation=landscape',
        description: 'Le plus grand édifice en terre crue au monde, chef-d\'œuvre.',
      },
      {
        name: 'Village des Pêcheurs',
        location: 'Ganvié, Bénin',
        image:
          'https://readdy.ai/api/search-image?query=Stilt%20village%20of%20Ganvie%20in%20Benin%20with%20wooden%20houses%20on%20lake%20water%20and%20wooden%20canoes%2C%20warm%20sunset%20light%2C%20authentic%20African%20lifestyle%2C%20professional%20travel%20photography%2C%20warm%20golden%20tones&width=600&height=450&seq=culture-tourism-3&orientation=landscape',
        description: 'La plus grande cité lacustre d\'Afrique, dite la Venise africaine.',
      },
    ],
  },
];